import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Tag, Space, Popconfirm, notification,
  Select, Modal, Image, Divider, Avatar, Input, Tooltip,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined,
  ReloadOutlined, EyeOutlined, CommentOutlined, LikeOutlined,
  SendOutlined, UserOutlined, EyeInvisibleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { TextArea } = Input;

const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const CAT_META = {
  news:     { label: 'समाचार',  color: 'blue'    },
  vacancy:  { label: 'रोजगार',  color: 'orange'  },
  thought:  { label: 'विचार',   color: 'purple'  },
  service:  { label: 'सेवा',    color: 'green'   },
  notice:   { label: 'घोषणा',   color: 'red'     },
};

const STATUS_COLOR  = { pending: 'gold', approved: 'green', rejected: 'red', hidden: 'default' };
const STATUS_LABEL  = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', hidden: 'Hidden' };

export default function AdminCommunityPosts() {
  const [data,           setData]           = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [statusF,        setStatusF]        = useState('pending');
  const [pagination,     setPagination]     = useState({ current: 1, pageSize: 20, total: 0 });
  const [preview,        setPreview]        = useState(null);
  const [commentText,    setCommentText]    = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchList = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: pagination.pageSize, status: statusF });
      const res = await axios.get(`${baseUrl}/admin/community-posts?${params}`, { headers: authH() });
      if (res.data.status) {
        setData(res.data.result);
        setPagination(p => ({ ...p, current: pg, total: res.data.total }));
      }
    } catch { notification.error({ message: 'Load failed' }); }
    finally { setLoading(false); }
  }, [statusF, pagination.pageSize]);

  useEffect(() => { fetchList(1); }, [statusF]); // eslint-disable-line

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${baseUrl}/admin/community-posts/${id}/status`, { status }, { headers: authH() });
      const msgs = { approved: '✅ Post Approved!', rejected: '❌ Post Rejected', hidden: '🙈 Post Hidden from Feed' };
      notification.success({ message: msgs[status] || 'Done' });
      fetchList(pagination.current);
      if (preview?.id === id) setPreview(prev => ({ ...prev, status }));
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Action failed' });
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${baseUrl}/admin/community-posts/${id}`, { headers: authH() });
      notification.success({ message: 'Deleted' });
      fetchList(pagination.current);
      if (preview?.id === id) setPreview(null);
    } catch { notification.error({ message: 'Delete failed' }); }
  };

  const openPreview = async (id) => {
    setLoadingPreview(true);
    setCommentText('');
    try {
      const res = await axios.get(`${baseUrl}/admin/community-posts/${id}`, { headers: authH() });
      if (res.data.status) setPreview(res.data.result);
    } catch { notification.error({ message: 'Load failed' }); }
    finally { setLoadingPreview(false); }
  };

  const postAdminComment = async () => {
    if (!commentText.trim() || !preview) return;
    setPostingComment(true);
    try {
      const res = await axios.post(
        `${baseUrl}/admin/community-posts/${preview.id}/comment`,
        { comment: commentText.trim() },
        { headers: authH() }
      );
      notification.success({ message: '💬 Comment posted!' });
      setCommentText('');
      // Refresh comments in preview
      const updated = await axios.get(`${baseUrl}/admin/community-posts/${preview.id}`, { headers: authH() });
      if (updated.data.status) setPreview(updated.data.result);
      fetchList(pagination.current);
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Failed to post comment' });
    } finally { setPostingComment(false); }
  };

  const columns = [
    {
      title: '#', dataIndex: 'id', width: 52, fixed: 'left',
      render: v => <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>{v}</span>,
    },
    {
      title: 'Post', dataIndex: 'title', width: 340, fixed: 'left',
      render: (t, r) => (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', maxWidth: 320 }}
          onClick={() => openPreview(r.id)}>
          {/* Thumbnail — fixed 52×52, only when image exists */}
          {r.image_url && (
            <img
              src={r.image_url} alt=""
              style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6,
                border: '1px solid #e5e7eb', flexShrink: 0 }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            {/* Title — max 2 lines */}
            <div style={{
              fontWeight: 700, fontSize: 13, color: '#111827',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', lineHeight: 1.4, marginBottom: 3,
            }}>
              {t || '(No Title)'}
            </div>
            {/* Excerpt — 1 line */}
            <div style={{
              fontSize: 11, color: '#6b7280', lineHeight: 1.4,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {(r.content || r.excerpt || '').replace(/\n/g, ' ')}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'श्रेणी', dataIndex: 'category', width: 90, align: 'center',
      render: c => { const m = CAT_META[c] || { label: c || '—', color: 'default' }; return <Tag color={m.color}>{m.label}</Tag>; },
    },
    {
      title: 'Author', dataIndex: 'author_name', width: 150,
      ellipsis: true,
      render: (n, r) => (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>{n || '—'}</div>
          <div style={{ color: '#9ca3af', fontSize: 11 }}>#{r.membership_no}</div>
        </div>
      ),
    },
    {
      title: 'Reactions', width: 80, align: 'center',
      render: (_, r) => (
        <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 2 }}>
          <div><LikeOutlined style={{ marginRight: 3, color: '#3b82f6' }} />{r.reactions || 0}</div>
          <div><CommentOutlined style={{ marginRight: 3, color: '#10b981' }} />{r.comments || 0}</div>
        </div>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 110, align: 'center',
      render: s => (
        <Tag color={STATUS_COLOR[s]} style={{ fontWeight: 600, margin: 0 }}>
          {s === 'hidden' && <EyeInvisibleOutlined style={{ marginRight: 4 }} />}
          {STATUS_LABEL[s] || s}
        </Tag>
      ),
    },
    {
      title: 'Date', dataIndex: 'created_at', width: 95, align: 'center',
      render: d => d
        ? <span style={{ fontSize: 12, color: '#6b7280' }}>
            {new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
          </span>
        : '—',
    },
    {
      title: 'Actions', width: 230,
      render: (_, r) => (
        <Space size={4} wrap>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openPreview(r.id)}>View</Button>
          {r.status !== 'approved' && (
            <Tooltip title="Approve — visible on home & community board">
              <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                style={{ background: '#15803d', borderColor: '#15803d' }}
                onClick={() => setStatus(r.id, 'approved')}>
                Approve
              </Button>
            </Tooltip>
          )}
          {r.status !== 'hidden' && (
            <Tooltip title="Hide from feed — post stays but is not shown on home or community board">
              <Popconfirm
                title="Hide this post from feed?"
                description="Post will not appear on home or community board. Author is not notified."
                okText="Yes, Hide" okButtonProps={{ style: { background: '#92400e', borderColor: '#92400e' } }}
                cancelText="Cancel"
                onConfirm={() => setStatus(r.id, 'hidden')}
              >
                <Button size="small" icon={<EyeInvisibleOutlined />}
                  style={{ color: '#92400e', borderColor: '#d97706', background: '#fffbeb' }}>
                  Hide
                </Button>
              </Popconfirm>
            </Tooltip>
          )}
          {r.status !== 'rejected' && (
            <Popconfirm
              title="Reject this post?"
              description="Post will be permanently rejected. Author may see this."
              okText="Yes, Reject" okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={() => setStatus(r.id, 'rejected')}
            >
              <Button size="small" danger icon={<CloseCircleOutlined />}>Reject</Button>
            </Popconfirm>
          )}
          <Popconfirm title="Delete permanently?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Card
        title={<span style={{ color: '#fff', fontWeight: 700 }}>Community Posts - समाज मंच</span>}
        headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        extra={
          <Space>
            <Select value={statusF} onChange={v => setStatusF(v)} style={{ width: 145 }}>
              <Select.Option value="pending"><Tag color="gold">Pending</Tag></Select.Option>
              <Select.Option value="approved"><Tag color="green">Approved</Tag></Select.Option>
              <Select.Option value="hidden"><Tag color="default"><EyeInvisibleOutlined /> Hidden</Tag></Select.Option>
              <Select.Option value="rejected"><Tag color="red">Rejected</Tag></Select.Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={() => fetchList(pagination.current)}>Refresh</Button>
          </Space>
        }
      >
        <style>{`
          .community-table .ant-table-row td { vertical-align: middle !important; }
          .community-table .ant-table-row:hover td { background: #f8faff !important; }
          .community-table .ant-table-thead th { background: #f1f5f9 !important; font-size: 12px; font-weight: 700; color: #374151; }
        `}</style>
        <Table
          className="community-table"
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 950 }}
          rowClassName={(_, i) => i % 2 === 0 ? '' : 'ant-table-row-striped'}
          style={{ '--stripe-bg': '#fafafa' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: pg => fetchList(pg),
            showTotal: t => `Total ${t}`,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
        />
      </Card>

      {/* Full post preview + comment modal */}
      <Modal
        open={!!preview || loadingPreview}
        onCancel={() => { setPreview(null); setCommentText(''); }}
        footer={null}
        width={720}
        title={
          preview ? (
            <Space>
              <Avatar src={preview.author_photo} icon={<UserOutlined />} size={36}>
                {(preview.author_name || '?')[0]}
              </Avatar>
              <div>
                <div style={{ fontWeight: 700, lineHeight: 1.2 }}>{preview.author_name}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  #{preview.membership_no} · {preview.author_village}
                </div>
              </div>
              <Tag color={CAT_META[preview.category]?.color}>{CAT_META[preview.category]?.label || preview.category}</Tag>
              <Tag color={STATUS_COLOR[preview.status]}>{preview.status}</Tag>
            </Space>
          ) : 'Loading...'
        }
      >
        {preview && (
          <div>
            {/* Post content */}
            <h3 style={{ margin: '0 0 12px', color: '#111827' }}>{preview.title}</h3>
            {preview.image_url && (
              <Image
                src={preview.image_url}
                style={{ width: '100%', maxHeight: 320, objectFit: 'cover', borderRadius: 8, marginBottom: 12 }}
              />
            )}
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.8, whiteSpace: 'pre-wrap', marginBottom: 12 }}>
              {preview.content}
            </div>
            {preview.tags && (
              <div style={{ marginBottom: 12 }}>
                {preview.tags.split(',').map(t => <Tag key={t} style={{ marginBottom: 4 }}>{t.trim()}</Tag>)}
              </div>
            )}

            {/* Approve / Hide / Reject actions */}
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Moderation Actions
              </div>
              <Space wrap>
                {preview.status !== 'approved' && (
                  <Tooltip title="Make visible on home & community board">
                    <Button type="primary" icon={<CheckCircleOutlined />}
                      style={{ background: '#15803d', borderColor: '#15803d' }}
                      onClick={() => setStatus(preview.id, 'approved')}>
                      Approve
                    </Button>
                  </Tooltip>
                )}
                {preview.status !== 'hidden' && (
                  <Tooltip title="Hide from home & feed — author is not notified of rejection">
                    <Popconfirm
                      title="Hide this post from feed?"
                      description="Post will not appear anywhere on the portal. Author is not notified."
                      okText="Yes, Hide" okButtonProps={{ style: { background: '#92400e', borderColor: '#92400e' } }}
                      onConfirm={() => setStatus(preview.id, 'hidden')}
                    >
                      <Button icon={<EyeInvisibleOutlined />}
                        style={{ color: '#92400e', borderColor: '#d97706', background: '#fffbeb' }}>
                        Hide from Feed
                      </Button>
                    </Popconfirm>
                  </Tooltip>
                )}
                {preview.status !== 'rejected' && (
                  <Popconfirm
                    title="Reject this post?"
                    description="Post will be permanently rejected."
                    okText="Yes, Reject" okButtonProps={{ danger: true }}
                    onConfirm={() => setStatus(preview.id, 'rejected')}
                  >
                    <Button danger icon={<CloseCircleOutlined />}>Reject</Button>
                  </Popconfirm>
                )}
                {preview.status === 'hidden' && (
                  <Tag color="default" style={{ padding: '4px 10px', fontSize: 12 }}>
                    <EyeInvisibleOutlined /> Currently hidden from all feeds
                  </Tag>
                )}
                <Popconfirm title="Delete permanently?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(preview.id)}>
                  <Button danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>
              </Space>
            </div>

            <Divider orientation="left" style={{ fontSize: 12, margin: '8px 0' }}>
              <CommentOutlined /> Comments ({preview.comments?.length || 0})
            </Divider>

            {/* Existing comments */}
            {(preview.comments || []).length === 0 && (
              <div style={{ color: '#9ca3af', fontSize: 12, textAlign: 'center', padding: '8px 0 12px' }}>
                कोई टिप्पणी नहीं — पहले टिप्पणी करें
              </div>
            )}
            {(preview.comments || []).map(c => (
              <div key={c.id} style={{
                display: 'flex', gap: 8, marginBottom: 10,
                opacity: c.is_delete ? 0.4 : 1,
                background: c.is_admin ? '#fef9c3' : '#f9fafb',
                borderRadius: 8, padding: '6px 10px',
              }}>
                <Avatar size={28} icon={<UserOutlined />}>{(c.author_name || '?')[0]}</Avatar>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{c.author_name}</span>
                  {c.is_admin && <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>Admin</Tag>}
                  {c.is_delete && <Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>Deleted</Tag>}
                  <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{c.comment}</div>
                  {c.created_at && (
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                      {new Date(c.created_at).toLocaleString('en-IN')}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Admin comment input */}
            <div style={{
              background: '#eff6ff', border: '1px solid #bfdbfe',
              borderRadius: 10, padding: '12px 14px', marginTop: 8,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', marginBottom: 8 }}>
                🏛️ Admin Comment (Office से टिप्पणी)
              </div>
              <TextArea
                rows={3}
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="यहाँ टिप्पणी लिखें..."
                maxLength={500}
                showCount
                style={{ marginBottom: 8 }}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={postingComment}
                disabled={!commentText.trim()}
                onClick={postAdminComment}
              >
                Post Comment as Admin
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
